import { useState, useEffect } from 'react';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import { PLATFORMS, getPlatform } from '../PlatformBadge/PlatformBadge';
import './LinkPlatformModal.css';

/**
 * Platforms that use a real OAuth round-trip on the backend. For those, we
 * skip the username field and kick off OAuth via onOAuthStart(platform).
 *
 * Platforms NOT in this set still use the temporary username-only flow until
 * their OAuth integration lands on the backend.
 */
const OAUTH_PLATFORMS = new Set(['instagram', 'tiktok', 'youtube', 'x', 'facebook']);

const LinkPlatformModal = ({
  open,
  onClose,
  onSubmit,
  onOAuthStart,
  existingPlatforms = [],
}) => {
  const [platform, setPlatform] = useState(null);
  const [username, setUsername] = useState('');

  useEffect(() => {
    if (open) {
      setPlatform(null);
      setUsername('');
    }
  }, [open]);

  const isOAuth = platform && OAUTH_PLATFORMS.has(platform);
  const selected = platform ? getPlatform(platform) : null;
  const SelectedIcon = selected?.icon;

  const handleSubmit = async () => {
    if (!platform) return;
    if (isOAuth) {
      await onOAuthStart(platform);
      return;
    }
    if (!username.trim()) return;
    await onSubmit({ platform, platformUsername: username.trim() });
    onClose();
  };

  const canSubmit = platform && (isOAuth || username.trim());

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Link a platform"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            icon={isOAuth && SelectedIcon ? <SelectedIcon /> : null}
          >
            {isOAuth ? `Continue to ${selected.label}` : 'Link'}
          </Button>
        </>
      }
    >
      <div className="link-platform-step">
        <div className="link-platform-label">Choose a platform</div>
        <div className="link-platform-options">
          {PLATFORMS.map((p) => {
            const Icon = p.icon;
            const already = existingPlatforms.includes(p.id);
            const comingSoon = !OAUTH_PLATFORMS.has(p.id);
            const disabled = already || comingSoon;
            const isSelected = platform === p.id;
            const title = already
              ? 'Already linked'
              : comingSoon
              ? `${p.label} linking is coming soon`
              : `Link ${p.label}`;
            return (
              <button
                key={p.id}
                type="button"
                className={[
                  'link-platform-option',
                  `link-platform-${p.id}`,
                  isSelected ? 'selected' : '',
                  disabled ? 'disabled' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => !disabled && setPlatform(p.id)}
                disabled={disabled}
                title={title}
              >
                <Icon />
                <span>{p.label}</span>
                {already && <span className="link-platform-chip">Linked</span>}
                {!already && comingSoon && (
                  <span className="link-platform-chip link-platform-chip-soon">
                    Coming soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {platform && isOAuth && (
        <div className="link-platform-step">
          <div className="link-platform-hint-block">
            You'll be taken to {selected.label} to approve access. When you come
            back, we'll pull your uploads and stats automatically.
            {platform === 'facebook' && (
              <>
                <br />
                <br />
                Heads up: Facebook will link the first Page you admin. If you
                manage multiple Pages and need a different one, reorder your
                Pages on Meta before continuing.
              </>
            )}
          </div>
        </div>
      )}

      {platform && !isOAuth && (
        <div className="link-platform-step">
          <div className="link-platform-label">
            Platform username
            <span className="link-platform-hint">
              (OAuth coming soon for this platform.)
            </span>
          </div>
          <input
            className="link-platform-input"
            autoFocus
            placeholder="@your_handle"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
};

export default LinkPlatformModal;
